import { useState, useMemo, useCallback, useEffect } from 'react';
import { useEpicRequestsPaged } from '@/hooks/queries/use-epic-requests';
import { useAuthStore } from '@/stores/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { usePageTitle } from '@/hooks/use-page-title';
import { useIsMobile } from '@/hooks/use-mobile';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, Plus, ChevronDown, Lightbulb, User } from 'lucide-react';
import { PageInfoButton } from '@/components/common/page-info-button';
import { RequestsScopeFilter } from '@/components/epic-requests/requests-scope-filter';
import type { ScopeMode } from '@/components/common/scope-selector';
import { cn } from '@/lib/utils';
import { ComplianceBadge } from '@/components/ui/compliance-badge';
import type { EpicRequestStatus, EpicRequestFilters } from '@/lib/api/epic-requests';
import { NewRequestButton } from '@/components/epic-requests/new-request-button';
import { InterviewHistory } from '@/components/epic-requests/interview-history';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { EpicRequestWithReactionCounts } from '@/lib/api/epic-requests';
import { EntityCard } from '@/components/ui/entity-card';
import { SortControl } from '@/components/common/sort-control';
import type { SortOption } from '@/components/common/sort-control';
import { PaginationControls, PAGE_SIZE_OPTIONS } from '@/components/common/pagination-controls';
import { getSortPreference, setSortPreference } from '@/lib/sort-preferences';

// Status badge colors based on status
const statusColors: Record<EpicRequestStatus, string> = {
  pending: 'bg-yellow-900/30 text-yellow-300',
  approved: 'bg-green-900/30 text-green-300',
  rejected: 'bg-red-900/30 text-red-300',
  converted: 'bg-blue-900/30 text-blue-300',
  planning: 'bg-purple-900/30 text-purple-300',
};

// Status order for grouping
const statusOrder: EpicRequestStatus[] = ['planning', 'pending', 'approved', 'converted', 'rejected'];

// Status hue colors for card glow
const statusHue: Record<EpicRequestStatus, string> = {
  pending: '#EAB308',
  approved: '#22C55E',
  rejected: '#EF4444',
  converted: '#3B82F6',
  planning: '#8B5CF6',
};

interface RequestCardProps {
  request: EpicRequestWithReactionCounts;
}

function RequestCard({ request }: RequestCardProps) {
  const hueColor = statusHue[request.status];
  const isPersonalScope = !!request.personalScopeId;

  const hoursAgo = Math.floor((Date.now() - new Date(request.createdAt).getTime()) / 3_600_000);
  const timeLabel = hoursAgo < 1 ? 'just now' : hoursAgo < 24 ? `${String(hoursAgo)} hour${hoursAgo === 1 ? '' : 's'} ago` : `${String(Math.floor(hoursAgo / 24))} day${Math.floor(hoursAgo / 24) === 1 ? '' : 's'} ago`;

  return (
    <EntityCard
      href={`/epic-requests/${request.id}`}
      hueColor={hueColor}
      icon={
        <Lightbulb className="h-5 w-5" style={{ color: hueColor }} />
      }
      title={request.identifier ? `${request.identifier} — ${request.title}` : request.title}
      subtitle={request.description ?? undefined}
      badge={isPersonalScope ? (
        <Badge className="gap-1 text-xs text-muted-foreground bg-white/[0.04] border border-white/[0.06]">
          <User className="h-3 w-3" />
          Personal
        </Badge>
      ) : undefined}
      user={request.requestedBy ? { name: request.requestedBy.name, avatarUrl: request.requestedBy.avatarUrl ?? null } : undefined}
      meta={`Submitted ${timeLabel}`}
      footer={
        <ComplianceBadge score={request.complianceScore ?? null} interactive={false} />
      }
    />
  );
}

const REQUEST_SORT_OPTIONS: SortOption[] = [
  { value: 'createdAt', label: 'Newest', direction: 'desc' },
  { value: 'createdAt', label: 'Oldest', direction: 'asc' },
  { value: 'updatedAt', label: 'Last updated', direction: 'desc' },
  { value: 'title', label: 'Title A-Z', direction: 'asc' },
];

const DEFAULT_REQUEST_SORT = { orderBy: 'createdAt', orderDirection: 'desc' as const };

export function EpicRequestsPage() {
  usePageTitle("Requests");
  const isMobile = useIsMobile();
  // Top-level view toggle: the request board vs. the interview transcript
  // history / reuse view (ENG-145).
  const [view, setView] = useState<'requests' | 'history'>('requests');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatuses, setSelectedStatusesState] = useState<EpicRequestStatus[]>(['pending', 'approved']);
  // The server filters and searches every request (not just the loaded page),
  // so a change starts again from page 1. Typing is debounced.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCursorStack([undefined]);
    }, 300);
    return () => { clearTimeout(timer); };
  }, [searchQuery]);
  const setSelectedStatuses = (statuses: EpicRequestStatus[]) => {
    setSelectedStatusesState(statuses);
    setCursorStack([undefined]);
  };

  // Sort state backed by localStorage
  const [sort, setSort] = useState(() =>
    getSortPreference('requestsSortPreference', DEFAULT_REQUEST_SORT, ['createdAt', 'updatedAt', 'title']),
  );

  const handleSortChange = (orderBy: string, orderDirection: 'asc' | 'desc') => {
    const newSort = { orderBy, orderDirection };
    setSort(newSort);
    setSortPreference('requestsSortPreference', newSort);
    setCursorStack([undefined]); // Reset to page 1
  };

  // Page size backed by localStorage
  const [pageSize, setPageSize] = useState(() => {
    try {
      const stored = localStorage.getItem('requestsPageSize');
      const parsed = stored ? parseInt(stored, 10) : NaN;
      return PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : 20;
    } catch { return 20; }
  });

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCursorStack([undefined]); // Reset to page 1
    try { localStorage.setItem('requestsPageSize', String(size)); } catch { /* quota/security */ }
  }, []);

  // Cursor stack for page-by-page navigation
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const currentPage = cursorStack.length;
  const currentCursor = cursorStack[cursorStack.length - 1];

  // Initialize scope from localStorage with 'my' as default
  const VALID_SCOPES: ScopeMode[] = ['my', 'all', 'team', 'user'];
  const [scope, setScope] = useState<ScopeMode>(() => {
    const stored = localStorage.getItem('requestsScope') as ScopeMode | null;
    return stored && VALID_SCOPES.includes(stored) ? stored : 'my';
  });

  const [scopeId, setScopeId] = useState<string | undefined>(() => {
    const stored = localStorage.getItem('requestsScopeId');
    return stored && stored !== '' ? stored : undefined;
  });

  const currentUser = useAuthStore((state) => state.user);

  const handleScopeChange = (newScope: ScopeMode, newScopeId?: string) => {
    setScope(newScope);
    setScopeId(newScopeId);
    setCursorStack([undefined]); // Reset to page 1
    localStorage.setItem('requestsScope', newScope);
    localStorage.setItem('requestsScopeId', newScopeId ?? '');
  };

  // Build filter parameters based on scope
  const requestFilters = useMemo(() => {
    const filters: EpicRequestFilters = {};

    if (scope === 'my' && currentUser?.id) {
      filters.requestedById = currentUser.id;
    } else if (scope === 'user' && scopeId) {
      filters.requestedById = scopeId;
    }
    // 'all' scope: no requestedById filter (show everything)
    // 'team' scope: not yet supported — treated same as 'all' until API supports team filtering

    // "planning" requests are always shown — it's a transient active state.
    if (selectedStatuses.length > 0) {
      filters.statuses = [...new Set<EpicRequestStatus>(['planning', ...selectedStatuses])].join(',');
    }
    if (debouncedSearch) {
      filters.search = debouncedSearch;
    }

    filters.orderBy = sort.orderBy as EpicRequestFilters['orderBy'];
    filters.orderDirection = sort.orderDirection;
    filters.limit = pageSize;
    if (currentCursor) {
      filters.cursor = currentCursor;
    }

    return filters;
  }, [scope, scopeId, currentUser?.id, sort, pageSize, currentCursor, selectedStatuses, debouncedSearch]);

  const { data, isLoading, isFetching } = useEpicRequestsPaged(requestFilters);

  // Extract page data and apply client-side filtering (status + search)
  const pageRequests = data?.data ?? [];
  const totalCount = data?.meta?.totalCount;
  const nextCursor = data?.meta?.cursor;
  const hasNextPage = Boolean(data?.meta?.hasMore && nextCursor);
  const hasPreviousPage = currentPage > 1;
  const totalPages = totalCount != null ? Math.ceil(totalCount / pageSize) : undefined;

  const handleNextPage = useCallback(() => {
    if (hasNextPage && nextCursor) {
      setCursorStack((prev) => [...prev, nextCursor]);
    }
  }, [hasNextPage, nextCursor]);

  const handlePreviousPage = useCallback(() => {
    setCursorStack((prev) => prev.length > 1 ? prev.slice(0, -1) : prev);
  }, []);
  
  // Status and search filtering happen on the server (requestFilters above).
  const filteredRequests = pageRequests;

  // Group requests by status
  const groupedRequests = filteredRequests.reduce<Partial<Record<EpicRequestStatus, EpicRequestWithReactionCounts[]>>>(
    (acc, request) => {
      const group = acc[request.status] ?? [];
      group.push(request);
      acc[request.status] = group;
      return acc;
    },
    {}
  );

  return (
    <div className="pt-4">
      <div className="glass-card rounded-2xl p-4 md:p-6">
      {/* Page title + Filters row on desktop */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <h1 className="text-xl font-semibold">Requests</h1>
            <PageInfoButton title="Requests" />
          </div>
          {/* View toggle: request board vs. interview transcript history (ENG-145) */}
          <Tabs value={view} onValueChange={(v) => { setView(v as 'requests' | 'history'); }}>
            <TabsList>
              <TabsTrigger value="requests">Requests</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Filters — stacked vertically on mobile, row on desktop */}
        {view === 'requests' && (
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          {/* Row 1: scope + new request */}
          <div className="flex items-center gap-2">
          <RequestsScopeFilter
            scope={scope}
            {...(scopeId !== undefined && { scopeId })}
            onScopeChange={handleScopeChange}
          />
          <div data-help-id="epicRequests.newRequest" className="flex items-center gap-2">
          <NewRequestButton>
            <Plus className="h-4 w-4 mr-2" />
            New Request
          </NewRequestButton>
          </div>
        </div>

        {/* Row 2: status filter + sort control */}
        <div className="flex items-center gap-2">
        <div data-help-id="epicRequests.statusFilter">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className={cn("justify-between font-normal", isMobile ? "w-full" : "w-[220px]")}
            >
              {selectedStatuses.length === 0
                ? 'All statuses'
                : selectedStatuses.length === 1 && selectedStatuses[0]
                ? `${selectedStatuses[0].charAt(0).toUpperCase()}${selectedStatuses[0].slice(1)}`
                : `${String(selectedStatuses.length)} statuses selected`}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[220px] p-3" align="start">
            <div className="space-y-2" role="presentation" onClick={(e) => { e.stopPropagation(); }}>
              {(['pending', 'approved', 'converted', 'rejected'] as EpicRequestStatus[]).map((status) => (
                <label
                  key={status}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-accent p-2 rounded-md"
                  onClick={(e) => { e.stopPropagation(); }}
                >
                  <Checkbox
                    checked={selectedStatuses.includes(status)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedStatuses([...selectedStatuses, status]);
                      } else {
                        setSelectedStatuses(selectedStatuses.filter((s) => s !== status));
                      }
                    }}
                  />
                  <span className="text-sm capitalize">
                    {status}
                  </span>
                </label>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
        <SortControl
          sortOptions={REQUEST_SORT_OPTIONS}
          activeSort={sort}
          onSortChange={handleSortChange}
        />
        </div>
        <div data-help-id="epicRequests.search" className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search requests..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); }}
            className="pl-9 md:pl-9"
          />
        </div>
        </div>
        )}
      </div>

      {view === 'history' && <InterviewHistory />}

      {/* Request list */}
      {view === 'requests' && (isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <div
              key={i}
              className="h-32 animate-pulse rounded-lg bg-muted"
            />
          ))}
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">
            {searchQuery
              ? 'No requests match your search'
              : scope !== 'all'
                ? 'No requests found for this filter. Try changing your filter selection or creating a new request.'
                : 'No requests yet'}
          </p>
          {!searchQuery && (
            <div className="flex items-center justify-center gap-2">
              <NewRequestButton>
                <Plus className="h-4 w-4 mr-2" />
                {scope !== 'all' ? 'Create Request' : 'Create your first request'}
              </NewRequestButton>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Grouped by status */}
          <div className="relative space-y-8">
            {statusOrder.map((status) => {
              const requestsInGroup = groupedRequests[status] ?? [];
              
              // Skip empty groups
              if (requestsInGroup.length === 0) {
                return null;
              }

              return (
                <div key={status}>
                  {/* Group header with status badge and count */}
                  <div className="flex items-center gap-2 mb-4">
                    <Badge
                      className={cn(
                        'text-xs font-medium',
                        statusColors[status]
                      )}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Badge>
                  </div>

                  {/* Grid of cards for this status group */}
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {requestsInGroup.map((request) => (
                      <RequestCard
                        key={request.id}
                        request={request}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Subtle loading overlay when navigating between pages */}
            {isFetching && !isLoading && (
              <div className="absolute inset-0 bg-background/30 rounded-2xl pointer-events-none" />
            )}
          </div>

          {/* Pagination controls */}
          <PaginationControls
            showing={filteredRequests.length}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            currentPage={currentPage}
            totalPages={totalPages}
            hasPreviousPage={hasPreviousPage}
            hasNextPage={hasNextPage}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            isLoading={isFetching}
          />
        </>
      ))}
      </div>
    </div>
  );
}
